import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import List from "@mui/icons-material/List";
import LooksOneOutlined from "@mui/icons-material/LooksOneOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Grid2 from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import classNames from "classnames";
import _ from "lodash";
import React, { useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import {
  ExtractionType,
  ParsingType,
  PredefinedExtraction,
  RegexMatch,
} from "../../../interfaces/doc";
import { ExtractionQuerySchemaType } from "../../../schemas/OrionConfig";
import FormTextField from "../../Common/FormTextField";
import ExtractionIcon from "./ConfigExtractionIcons";
import {
  useConfigModeContext,
  useFormConfigContext,
} from "./ConfigFormContext";

interface ConfigExtractionQueryProps {
  index: number;
  groupIndex?: number;
  removeQuery: Function;
}

const ConfigExtractionQuery: React.FC<ConfigExtractionQueryProps> = ({
  index,
  groupIndex,
  removeQuery,
}) => {
  const { control, setValue, watch } = useFormConfigContext();
  const { isReadOnly } = useConfigModeContext();
  const [open, setOpen] = useState<boolean>(false);
  const queryPath =
    groupIndex !== undefined
      ? (`config.extraction.queries.${index}.queries.${groupIndex}` as const)
      : (`config.extraction.queries.${index}` as const);

  const query = watch(`${queryPath}`) as ExtractionQuerySchemaType;

  const {
    fields: customFields,
    append: addField,
    remove: removeField,
  } = useFieldArray({
    control,
    name: `${queryPath}.custom_schema`,
  });

  const getContent = () => {
    if (query.type === ExtractionType.PREDEFINED)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                {!isReadOnly && (
                  <Button color="error" onClick={() => removeQuery()}>
                    <Delete color="error" />
                  </Button>
                )}
              </div>
            </div>
            <Typography fontSize={12}>{query.query}</Typography>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <FormControl>
              <FormLabel id="query_label">Choix de l'extraction</FormLabel>
              <Controller
                control={control}
                name={`${queryPath}.query`}
                render={({ field }) => (
                  <Select
                    size="small"
                    className="flex flex-1"
                    labelId="query_label"
                    {...field}
                  >
                    {_.values(PredefinedExtraction).map((queryType) => (
                      <MenuItem key={queryType} value={queryType}>
                        {queryType}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Stack>
        );
      }
    else if (query.type === ExtractionType.LLM)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                {!isReadOnly && (
                  <Button color="error" onClick={() => removeQuery()}>
                    <Delete color="error" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              {query.many && <List fontSize="small" />}
              <Typography fontSize={12} className="truncate">
                {query.query}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <Grid2 container spacing={2}>
              <Grid2 size={2}>
                <Controller
                  control={control}
                  name={`${queryPath}.parsing_type`}
                  render={({ field }) => (
                    <Select
                      size="small"
                      fullWidth
                      {...field}
                      onChange={(e) => {
                        if (e.target.value === ParsingType.Custom) {
                          setValue(`${queryPath}.custom_schema`, [
                            { name: "", parsing_type: ParsingType.String },
                          ]);
                        } else {
                          setValue(`${queryPath}.custom_schema`, undefined);
                        }
                        if (e.target.value === ParsingType.Enum) {
                          setValue(`${queryPath}.enum_values`, []);
                        } else {
                          setValue(`${queryPath}.enum_values`, undefined);
                        }
                        field.onChange(e.target.value);
                      }}
                    >
                      {_.values(ParsingType).map((parsingType) => (
                        <MenuItem key={parsingType} value={parsingType}>
                          <div className="flex items-center gap-1">
                            <ExtractionIcon
                              parsingType={parsingType}
                              props={{ fontSize: "small" }}
                            />
                            {parsingType.charAt(0).toUpperCase() +
                              parsingType.slice(1)}
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid2>
              <Grid2 size={8}>
                <FormTextField
                  fullWidth
                  size="small"
                  control={control}
                  name={`${queryPath}.query`}
                  multiline
                  label="Extraction"
                  placeholder="Décrivez l'information que vous souhaitez extraire"
                />
              </Grid2>
              <Grid2 size={2}>
                <Controller
                  name={`${queryPath}.many`}
                  control={control}
                  render={({ field: { onChange, value, ...props } }) => (
                    <div className="flex items-center gap-2 border rounded-lg p-1 h-full">
                      <Button
                        disabled={isReadOnly}
                        {...props}
                        onClick={() => onChange(false)}
                        className={classNames(
                          "flex items-center gap-1 px-3 py-1.5 rounded",
                          {
                            "bg-blue-100 text-blue-700": !value && !isReadOnly,
                            "bg-gray-100 text-gray-700": !value && isReadOnly,
                            "text-gray-600 hover:bg-gray-100":
                              value && !isReadOnly,
                            "text-gray-600": value && isReadOnly,
                          }
                        )}
                      >
                        <LooksOneOutlined className="w-4 h-4" />
                        <span>Unique</span>
                      </Button>
                      <Button
                        disabled={isReadOnly}
                        {...props}
                        onClick={() => onChange(true)}
                        className={classNames(
                          "flex items-center gap-1 px-3 py-1.5 rounded",
                          {
                            "bg-blue-100 text-blue-700": value && !isReadOnly,
                            "bg-gray-100 text-gray-700": value && isReadOnly,
                            "text-gray-600 hover:bg-gray-100":
                              !value && !isReadOnly,
                            "text-gray-600": !value && isReadOnly,
                          }
                        )}
                      >
                        <List className="w-4 h-4" />
                        <span>Multiple</span>
                      </Button>
                    </div>
                  )}
                />
              </Grid2>
            </Grid2>
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Enum && (
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <Controller
                      name={`${queryPath}.enum_values`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Box>
                          <TextField
                            placeholder="Enter a value and press Enter"
                            onKeyDown={(
                              event: React.KeyboardEvent<HTMLInputElement>
                            ) => {
                              if (
                                event.key === "Enter" &&
                                event.currentTarget.value.trim() !== ""
                              ) {
                                onChange([
                                  ...(value || []),
                                  event.currentTarget.value.trim(),
                                ]);
                                event.currentTarget.value = "";
                                event.preventDefault();
                              }
                            }}
                            fullWidth
                            label="Options"
                            size="small"
                          />
                          <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                            {(value || []).map((val: string, index: number) => (
                              <Chip
                                key={index}
                                label={val}
                                onDelete={
                                  isReadOnly
                                    ? undefined
                                    : () => {
                                        onChange(
                                          !!value
                                            ? value.filter(
                                                (_: string, i: number) =>
                                                  i !== index
                                              )
                                            : value
                                        );
                                      }
                                }
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    />
                  </Grid2>
                </Grid2>
              )}
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Custom && (
                <Stack spacing={2}>
                  <Typography variant="subtitle1" className="font-semibold">
                    Champs personnalisés
                  </Typography>

                  {customFields.map((field, fieldIndex) => {
                    const field_parsing_type = watch(
                      `${queryPath}.custom_schema.${fieldIndex}.parsing_type`
                    );

                    return (
                      <Paper key={field.id} variant="outlined" className="p-2">
                        <Grid2 container spacing={2} alignItems="center">
                          {/* Parsing Type */}
                          <Grid2 size={2}>
                            <Controller
                              control={control}
                              name={`${queryPath}.custom_schema.${fieldIndex}.parsing_type`}
                              render={({ field }) => (
                                <Select size="small" fullWidth {...field}>
                                  {[
                                    ParsingType.String,
                                    ParsingType.Int,
                                    ParsingType.Boolean,
                                    ParsingType.Float,
                                    ParsingType.Enum,
                                  ].map((parsingType) => (
                                    <MenuItem
                                      key={parsingType}
                                      value={parsingType}
                                    >
                                      <div className="flex items-center gap-1">
                                        <ExtractionIcon
                                          parsingType={parsingType}
                                          props={{ fontSize: "small" }}
                                        />
                                        {parsingType}
                                      </div>
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </Grid2>
                          {/* Field Name */}
                          <Grid2 size={8}>
                            <FormTextField
                              size="small"
                              label="Nom du champ"
                              name={`${queryPath}.custom_schema.${fieldIndex}.name`}
                              control={control}
                              fullWidth
                            />
                          </Grid2>

                          {/* Action Buttons */}
                          {!isReadOnly && (
                            <Grid2 size={2}>
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                <Button
                                  color="info"
                                  onClick={() =>
                                    addField({
                                      name: "",
                                      parsing_type: ParsingType.String,
                                    })
                                  }
                                  size="small"
                                  sx={{
                                    display:
                                      fieldIndex === customFields.length - 1
                                        ? "visible"
                                        : "none",
                                  }}
                                >
                                  <Add fontSize="small" />
                                </Button>

                                <Button
                                  color="error"
                                  onClick={() => removeField(fieldIndex)}
                                  size="small"
                                >
                                  <Delete fontSize="small" />
                                </Button>
                              </Stack>
                            </Grid2>
                          )}

                          {/* Enum Values - Shown only when parsing type is Enum */}
                          {field_parsing_type === ParsingType.Enum && (
                            <Grid2 size={12} className="mt-2">
                              <Controller
                                name={`${queryPath}.custom_schema.${fieldIndex}.enum_values`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <Box>
                                    <TextField
                                      placeholder="Enter a value and press Enter"
                                      onKeyDown={(
                                        event: React.KeyboardEvent<HTMLInputElement>
                                      ) => {
                                        if (
                                          event.key === "Enter" &&
                                          event.currentTarget.value.trim() !==
                                            ""
                                        ) {
                                          onChange([
                                            ...(value || []),
                                            event.currentTarget.value.trim(),
                                          ]);
                                          event.currentTarget.value = "";
                                          event.preventDefault();
                                        }
                                      }}
                                      fullWidth
                                      label="Options"
                                      size="small"
                                    />
                                    <Box
                                      mt={2}
                                      display="flex"
                                      flexWrap="wrap"
                                      gap={1}
                                    >
                                      {(value || []).map(
                                        (val: string, index: number) => (
                                          <Chip
                                            key={index}
                                            label={val}
                                            onDelete={
                                              isReadOnly
                                                ? undefined
                                                : () => {
                                                    onChange(
                                                      !!value
                                                        ? value.filter(
                                                            (
                                                              _: string,
                                                              i: number
                                                            ) => i !== index
                                                          )
                                                        : value
                                                    );
                                                  }
                                            }
                                          />
                                        )
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              />
                            </Grid2>
                          )}
                        </Grid2>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
          </Stack>
        );
      }
    else if (query.type === ExtractionType.REGEX)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                {!isReadOnly && (
                  <Button color="error" onClick={() => removeQuery()}>
                    <Delete color="error" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              {query.many && <List fontSize="small" />}
              <Typography fontSize={12} className="truncate">
                {query.pattern}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <Grid2 container spacing={2}>
              <Grid2 size={2}>
                <Controller
                  control={control}
                  name={`${queryPath}.parsing_type`}
                  render={({ field }) => (
                    <Select
                      size="small"
                      className="flex flex-1"
                      {...field}
                      onChange={(e) => {
                        if (e.target.value === ParsingType.Custom) {
                          setValue(`${queryPath}.custom_schema`, [
                            { name: "", parsing_type: ParsingType.String },
                          ]);
                        } else {
                          setValue(`${queryPath}.custom_schema`, undefined);
                        }
                        if (e.target.value === ParsingType.Enum) {
                          setValue(`${queryPath}.enum_values`, []);
                        } else {
                          setValue(`${queryPath}.enum_values`, undefined);
                        }
                        field.onChange(e.target.value);
                      }}
                    >
                      {_.values(ParsingType).map((parsingType) => (
                        <MenuItem key={parsingType} value={parsingType}>
                          <div className="flex items-center gap-1">
                            <ExtractionIcon
                              parsingType={parsingType}
                              props={{ fontSize: "small" }}
                            />
                            {parsingType}
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid2>
              <Grid2 size={10}>
                <FormTextField
                  className="flex"
                  size="small"
                  control={control}
                  name={`${queryPath}.pattern`}
                  multiline
                  label="Expression régulière"
                />
              </Grid2>
              <Grid2 size={2}>
                <Controller
                  name={`${queryPath}.many`}
                  control={control}
                  render={({ field: { onChange, value, ...props } }) => (
                    <div className="flex items-center gap-2 border rounded-lg p-1 w-fit">
                      <Button
                        disabled={isReadOnly}
                        {...props}
                        onClick={() => {
                          onChange(false);
                          setValue(
                            `${queryPath}.params.match`,
                            RegexMatch.FIRST
                          );
                        }}
                        className={classNames(
                          "flex items-center gap-1 px-3 py-1.5 rounded",
                          {
                            "bg-blue-100 text-blue-700": !value && !isReadOnly,
                            "bg-gray-100 text-gray-700": !value && isReadOnly,
                            "text-gray-600 hover:bg-gray-100":
                              value && !isReadOnly,
                            "text-gray-600": value && isReadOnly,
                          }
                        )}
                      >
                        <LooksOneOutlined className="w-4 h-4" />
                        <span>Unique</span>
                      </Button>
                      <Button
                        disabled={isReadOnly}
                        {...props}
                        onClick={() => {
                          onChange(true);
                          setValue(`${queryPath}.params.match`, RegexMatch.ALL);
                        }}
                        className={classNames(
                          "flex items-center gap-1 px-3 py-1.5 rounded",
                          {
                            "bg-blue-100 text-blue-700": value && !isReadOnly,
                            "bg-gray-100 text-gray-700": value && isReadOnly,
                            "text-gray-600 hover:bg-gray-100":
                              !value && !isReadOnly,
                            "text-gray-600": !value && isReadOnly,
                          }
                        )}
                      >
                        <List className="w-4 h-4" />
                        <span>Multiple</span>
                      </Button>
                    </div>
                  )}
                />
              </Grid2>
            </Grid2>
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Enum && (
                <Controller
                  name={`${queryPath}.enum_values`}
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const handleAdd = (
                      event: React.KeyboardEvent<HTMLInputElement>
                    ) => {
                      if (
                        event.key === "Enter" &&
                        event.currentTarget.value.trim() !== ""
                      ) {
                        onChange([
                          ...(value || []),
                          event.currentTarget.value.trim(),
                        ]);
                        event.currentTarget.value = "";
                        event.preventDefault();
                      }
                    };

                    const handleDelete = (index: number) => {
                      onChange(
                        !!value
                          ? value.filter((_: string, i: number) => i !== index)
                          : value
                      );
                    };

                    return (
                      <Box>
                        <TextField
                          placeholder="Enter a value and press Enter"
                          onKeyDown={handleAdd}
                          fullWidth
                          label="Options"
                          size="small"
                        />
                        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                          {(value || []).map((val: string, index: number) => (
                            <Chip
                              key={index}
                              label={val}
                              onDelete={
                                isReadOnly
                                  ? undefined
                                  : () => handleDelete(index)
                              }
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  }}
                />
              )}
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Custom && (
                <Stack spacing={2}>
                  <Typography variant="subtitle1" className="font-semibold">
                    Champs personnalisés
                  </Typography>

                  {customFields.map((field, fieldIndex) => {
                    const field_parsing_type = watch(
                      `${queryPath}.custom_schema.${fieldIndex}.parsing_type`
                    );

                    return (
                      <Paper key={field.id} variant="outlined" className="p-2">
                        <Grid2 container spacing={2} alignItems="center">
                          {/* Parsing Type */}
                          <Grid2 size={2}>
                            <Controller
                              control={control}
                              name={`${queryPath}.custom_schema.${fieldIndex}.parsing_type`}
                              render={({ field }) => (
                                <Select size="small" fullWidth {...field}>
                                  {[
                                    ParsingType.String,
                                    ParsingType.Int,
                                    ParsingType.Boolean,
                                    ParsingType.Float,
                                    ParsingType.Enum,
                                  ].map((parsingType) => (
                                    <MenuItem
                                      key={parsingType}
                                      value={parsingType}
                                    >
                                      <div className="flex items-center gap-1">
                                        <ExtractionIcon
                                          parsingType={parsingType}
                                          props={{ fontSize: "small" }}
                                        />
                                        {parsingType}
                                      </div>
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </Grid2>
                          {/* Field Name */}
                          <Grid2 size={8}>
                            <FormTextField
                              size="small"
                              label="Nom du champ"
                              name={`${queryPath}.custom_schema.${fieldIndex}.name`}
                              control={control}
                              fullWidth
                            />
                          </Grid2>

                          {/* Action Buttons */}
                          {!isReadOnly && (
                            <Grid2 size={2}>
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                <Button
                                  color="info"
                                  onClick={() =>
                                    addField({
                                      name: "",
                                      parsing_type: ParsingType.String,
                                    })
                                  }
                                  size="small"
                                  sx={{
                                    display:
                                      fieldIndex === customFields.length - 1
                                        ? "visible"
                                        : "none",
                                  }}
                                >
                                  <Add fontSize="small" />
                                </Button>

                                <Button
                                  color="error"
                                  onClick={() => removeField(fieldIndex)}
                                  size="small"
                                >
                                  <Delete fontSize="small" />
                                </Button>
                              </Stack>
                            </Grid2>
                          )}

                          {/* Enum Values - Shown only when parsing type is Enum */}
                          {field_parsing_type === ParsingType.Enum && (
                            <Grid2 size={12} className="mt-2">
                              <Controller
                                name={`${queryPath}.custom_schema.${fieldIndex}.enum_values`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <Box>
                                    <TextField
                                      placeholder="Enter a value and press Enter"
                                      onKeyDown={(
                                        event: React.KeyboardEvent<HTMLInputElement>
                                      ) => {
                                        if (
                                          event.key === "Enter" &&
                                          event.currentTarget.value.trim() !==
                                            ""
                                        ) {
                                          onChange([
                                            ...(value || []),
                                            event.currentTarget.value.trim(),
                                          ]);
                                          event.currentTarget.value = "";
                                          event.preventDefault();
                                        }
                                      }}
                                      fullWidth
                                      label="Options"
                                      size="small"
                                    />
                                    <Box
                                      mt={2}
                                      display="flex"
                                      flexWrap="wrap"
                                      gap={1}
                                    >
                                      {(value || []).map(
                                        (val: string, index: number) => (
                                          <Chip
                                            key={index}
                                            label={val}
                                            onDelete={
                                              isReadOnly
                                                ? undefined
                                                : () => {
                                                    onChange(
                                                      !!value
                                                        ? value.filter(
                                                            (
                                                              _: string,
                                                              i: number
                                                            ) => i !== index
                                                          )
                                                        : value
                                                    );
                                                  }
                                            }
                                          />
                                        )
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              />
                            </Grid2>
                          )}
                        </Grid2>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            <Grid2
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Grid2 size={6}>
                <Controller
                  control={control}
                  name={`${queryPath}.params.match`}
                  render={({ field }) => (
                    <Select
                      size="small"
                      labelId="query_label"
                      fullWidth
                      {...field}
                      onChange={(e) => {
                        if (e.target.value === RegexMatch.ALL) {
                          setValue(`${queryPath}.many`, true);
                        } else {
                          setValue(`${queryPath}.many`, false);
                        }
                        if (e.target.value === RegexMatch.EXISTS) {
                          setValue(
                            `${queryPath}.parsing_type`,
                            ParsingType.Boolean
                          );
                        }
                        field.onChange(e.target.value);
                      }}
                    >
                      {_.values(RegexMatch).map((match) => (
                        <MenuItem key={match} value={match}>
                          {match}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid2>
              <Grid2 size={6}>
                <FormTextField
                  fullWidth
                  size="small"
                  label="Numéro du groupe"
                  name={`${queryPath}.params.group_number`}
                  handleChange={(e, fieldOnChange: (e: any) => void) =>
                    fieldOnChange(
                      e.target.value
                        ? Number(e.target.value.replace(/\D/, ""))
                        : null
                    )
                  }
                  control={control}
                  type="number"
                  rules={{ valueAsNumber: true }}
                />
              </Grid2>
            </Grid2>
          </Stack>
        );
      }
    else if (
      query.type === ExtractionType.COMPUTED ||
      query.type === ExtractionType.AGGREGATE
    )
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                {!isReadOnly && (
                  <Button color="error" onClick={() => removeQuery()}>
                    <Delete color="error" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              <Typography fontSize={12} className="truncate">
                {query.expression}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <Grid2 container spacing={2}>
              <Grid2 size={2}>
                <Controller
                  control={control}
                  name={`${queryPath}.parsing_type`}
                  render={({ field }) => (
                    <Select
                      size="small"
                      className="flex flex-1"
                      {...field}
                      onChange={(e) => {
                        if (e.target.value === ParsingType.Custom) {
                          setValue(`${queryPath}.custom_schema`, [
                            { name: "", parsing_type: ParsingType.String },
                          ]);
                        } else {
                          setValue(`${queryPath}.custom_schema`, undefined);
                        }
                        field.onChange(e.target.value);
                      }}
                    >
                      {_.values(ParsingType)
                        .filter(
                          (parsingType) => parsingType !== ParsingType.Enum
                        )
                        .map((parsingType) => (
                          <MenuItem key={parsingType} value={parsingType}>
                            <div className="flex items-center gap-1">
                              <ExtractionIcon
                                parsingType={parsingType}
                                props={{ fontSize: "small" }}
                              />
                              {parsingType.charAt(0).toUpperCase() +
                                parsingType.slice(1)}
                            </div>
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                />
              </Grid2>
              <Grid2 size={10}>
                <FormTextField
                  className="flex"
                  size="small"
                  control={control}
                  name={`${queryPath}.expression`}
                  multiline
                  label="Expression"
                  placeholder="Décrivez l'information que vous souhaitez extraire en JSONata"
                />
              </Grid2>
            </Grid2>
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Custom && (
                <Stack spacing={2}>
                  <Typography variant="subtitle1" className="font-semibold">
                    Champs personnalisés
                  </Typography>

                  {customFields.map((field, fieldIndex) => {
                    const field_parsing_type = watch(
                      `${queryPath}.custom_schema.${fieldIndex}.parsing_type`
                    );

                    return (
                      <Paper key={field.id} variant="outlined" className="p-2">
                        <Grid2 container spacing={2} alignItems="center">
                          {/* Parsing Type */}
                          <Grid2 size={2}>
                            <Controller
                              control={control}
                              name={`${queryPath}.custom_schema.${fieldIndex}.parsing_type`}
                              render={({ field }) => (
                                <Select size="small" fullWidth {...field}>
                                  {[
                                    ParsingType.String,
                                    ParsingType.Int,
                                    ParsingType.Boolean,
                                    ParsingType.Float,
                                    ParsingType.Enum,
                                  ].map((parsingType) => (
                                    <MenuItem
                                      key={parsingType}
                                      value={parsingType}
                                    >
                                      <div className="flex items-center gap-1">
                                        <ExtractionIcon
                                          parsingType={parsingType}
                                          props={{ fontSize: "small" }}
                                        />
                                        {parsingType}
                                      </div>
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </Grid2>
                          {/* Field Name */}
                          <Grid2 size={8}>
                            <FormTextField
                              size="small"
                              label="Nom du champ"
                              name={`${queryPath}.custom_schema.${fieldIndex}.name`}
                              control={control}
                              fullWidth
                            />
                          </Grid2>

                          {/* Action Buttons */}
                          {!isReadOnly && (
                            <Grid2 size={2}>
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                <Button
                                  color="info"
                                  onClick={() =>
                                    addField({
                                      name: "",
                                      parsing_type: ParsingType.String,
                                    })
                                  }
                                  size="small"
                                  sx={{
                                    display:
                                      fieldIndex === customFields.length - 1
                                        ? "visible"
                                        : "none",
                                  }}
                                >
                                  <Add fontSize="small" />
                                </Button>

                                <Button
                                  color="error"
                                  onClick={() => removeField(fieldIndex)}
                                  size="small"
                                >
                                  <Delete fontSize="small" />
                                </Button>
                              </Stack>
                            </Grid2>
                          )}

                          {/* Enum Values - Shown only when parsing type is Enum */}
                          {field_parsing_type === ParsingType.Enum && (
                            <Grid2 size={12} className="mt-2">
                              <Controller
                                name={`${queryPath}.custom_schema.${fieldIndex}.enum_values`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <Box>
                                    <TextField
                                      placeholder="Enter a value and press Enter"
                                      onKeyDown={(
                                        event: React.KeyboardEvent<HTMLInputElement>
                                      ) => {
                                        if (
                                          event.key === "Enter" &&
                                          event.currentTarget.value.trim() !==
                                            ""
                                        ) {
                                          onChange([
                                            ...(value || []),
                                            event.currentTarget.value.trim(),
                                          ]);
                                          event.currentTarget.value = "";
                                          event.preventDefault();
                                        }
                                      }}
                                      fullWidth
                                      label="Options"
                                      size="small"
                                    />
                                    <Box
                                      mt={2}
                                      display="flex"
                                      flexWrap="wrap"
                                      gap={1}
                                    >
                                      {(value || []).map(
                                        (val: string, index: number) => (
                                          <Chip
                                            key={index}
                                            label={val}
                                            onDelete={
                                              isReadOnly
                                                ? undefined
                                                : () => {
                                                    onChange(
                                                      !!value
                                                        ? value.filter(
                                                            (
                                                              _: string,
                                                              i: number
                                                            ) => i !== index
                                                          )
                                                        : value
                                                    );
                                                  }
                                            }
                                          />
                                        )
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              />
                            </Grid2>
                          )}
                        </Grid2>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
          </Stack>
        );
      }
    else return null;
  };

  return (
    <Paper
      square={false}
      elevation={3}
      className={classNames(
        "flex flex-col gap-2 p-2 border-[var(--mui-palette-primary-main)] border-solid	border-1",
        {
          "hover:cursor-pointer": !open,
        }
      )}
      onClick={!open ? () => setOpen(true) : undefined}
    >
      {getContent()}
    </Paper>
  );
};
export default ConfigExtractionQuery;
